import {getCountryCode, getLanguage} from '@smart/locale-service';
import type {TextProps} from '@smart/react-components';
import {Button, Markdown, Notification, Text} from '@smart/react-components';
import type {FormEventHandler, ReactNode} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import {removeOptionalKeysSuffix} from '../../../../utils/removeOptionalKeysSuffix';
import {transformMarkdown} from '../../../../utils/transform-markdown';
import FormStyles from '../../common/form-styles.module.scss';
import {scrollToRef} from '../../common/helpers';
import type {
  CountryCode,
  LangCode,
  ViewsFetchingToPostForm,
  availableCountries,
} from '../../common/types';
import type {PhoneCode} from '../../common/types';
import {AppModelContext} from '../../components/app';
import {CountrySelect} from '../../components/country-select';
import Fields from '../../components/easy-form/fields';
import type {WrapperHandler} from '../../components/easy-form/types';
import {MktLangSelect} from '../../components/mkt-lang-select';
import PhoneCodeSelect from '../../components/phone-code-select';
import PhoneField from '../../components/phone-field';
import {PopupContainer} from '../../components/popup-container';
import {useAPI} from '../../hooks/use-api';
import {useFeatureAppEnvHook} from '../../hooks/use-feature-app-env';
import {useLocale} from '../../hooks/use-locale';
import {useSchemas} from '../../hooks/user-schemas';
import {useAnalytics} from '../../utils/analytics';
import localStyles from './styles.module.scss';

type SubscribeProps = {
  renderAsPopup?: boolean;
};
export interface ISubscribeState {
  firstName: string;
  lastName: string;
  email: string;
  preferredLang: LangCode;
  country?: CountryCode;
  phoneCode?: PhoneCode;
  phoneNumber?: string;
  postalCode?: string;
  campaignId?: string;
  eventName?: string;
  productInterest?: string;
  lineInterests?: string;
  colorInterest?: string;
  noPreference?: boolean;
}

const viewName: ViewsFetchingToPostForm = `subscribe-form`;

const renderMultiline = (
  text: string,
  variant: TextProps['variant'],
  className?: string,
): ReactNode => (
  <>
    {text
      .split(`\n`)
      .filter((line) => !!line)
      .map((line, index) => (
        <Text className={className || ``} variant={variant} key={index} as="p">
          {line}
        </Text>
      ))}
  </>
);
// eslint-disable-next-line complexity
export default function Subscribe({
  renderAsPopup,
}: SubscribeProps): JSX.Element {
  const [hasErrors, setHasErrors] = useState(true);
  const [formData, setFormData] = useState<ISubscribeState>();
  const [mkt, setMkt] = useState<CountryCode>();
  const formWrapperRef = useRef<HTMLDivElement>(null);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const {campaignId} = React.useContext(AppModelContext);
  const trackEvent = useAnalytics(campaignId);
  const {
    featureServices: {
      'pub-sub': {subscribe},
    },
  } = useFeatureAppEnvHook();

  const optionalPopupSuffix = renderAsPopup ? `-popup` : ``;

  const apiFetch = useAPI(viewName, true);
  const {languageTag, marketId} = useLocale();
  const countryCode = getCountryCode(marketId);
  const language = getLanguage(languageTag);
  const changeHandler: WrapperHandler = (state, errors) => {
    setHasErrors(!!errors);
    setFormData(state as unknown as ISubscribeState);
    const selectedMKT = (state as any)?.country as CountryCode | undefined;
    if (!!selectedMKT) {
      setMkt(selectedMKT);
    } else if (!!mkt) {
      setMkt(undefined);
    }
  };
  const generalSchemas = useSchemas();
  const schemas = useMemo(() => {
    const {email, firstAndLastNames, postalCode} = generalSchemas;
    return {
      firstName: firstAndLastNames(true),
      lastName: firstAndLastNames(true),
      email: email(true),
      postalCode: postalCode(marketId, false),
    };
  }, [generalSchemas, formData?.country]);
  const cmsSubscribeTranslations =
    React.useContext(AppModelContext).cms.formsFeatureAppSubscribeUi || {};
  const apiTranslations =
    React.useContext(AppModelContext).cms.apiErrorMessageKeys || {};

  const {
    introHeader,
    introText,
    thankYouHeader,
    thankYouText,
    enablePhone,
    enablePostalCode,
    eventName,
    productInterest,
  } =
    campaignId && campaignId in cmsSubscribeTranslations.campaigns
      ? cmsSubscribeTranslations.campaigns[campaignId]
      : cmsSubscribeTranslations.campaigns[`default`];

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (hasErrors) {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
      return;
    }

    if (submitted) {
      setSubmitted(false);
      return;
    }

    if (!formData) return;

    setLoading(true);
    setServerError(undefined);

    try {
      await apiFetch(
        renderAsPopup
          ? (removeOptionalKeysSuffix(
              formData,
              optionalPopupSuffix,
            ) as ISubscribeState)
          : formData,
      );
      setSubmitted(true);
      trackEvent(`success`, `subscribe`);
    } catch (error) {
      setServerError(error as string);
      scrollToRef(formWrapperRef);
      trackEvent(`error`, `subscribe`);
    } finally {
      setLoading(false);
    }
  };

  const setValueFromAEM = () => {
    let apiErrorMessage = ``;
    let translatedString;

    const getStringFromAEM = apiTranslations.errorMessageKeys?.filter((e) => {
      return e?.includes(serverError as any);
    }, serverError);

    if (!getStringFromAEM) {
      apiErrorMessage = apiTranslations.generalFallbackErrorMessage as string;
    } else {
      translatedString = getStringFromAEM && getStringFromAEM[0]?.split(`:`);
      apiErrorMessage = translatedString && (translatedString[1] as any);
    }
    return apiErrorMessage;
  };

  const subscribeForm = (
    <div className={localStyles.wrapper} id="subscribe-form">
      <div className={localStyles.col1}>
        {renderMultiline(
          submitted ? thankYouHeader : introHeader,
          `label-100`,
          localStyles.header,
        )}
        {renderMultiline(submitted ? thankYouText : introText, `p-200`)}
      </div>
      <div className={localStyles.col2} ref={formWrapperRef}>
        <Fields.Wrapper
          translationPrefix={viewName}
          onUpdate={changeHandler}
          showErrors={submitAttempt}
          disabled={loading}
        >
          {serverError && (
            <Notification
              signal="error"
              className={FormStyles.serverError}
              onOpenChange={(open) => {
                if (!open) {
                  setServerError(undefined);
                }
              }}
            >
              <Notification.Text>{setValueFromAEM()}</Notification.Text>
            </Notification>
          )}

          <form onSubmit={onSubmit}>
            <fieldset className={localStyles.fieldset}>
              <div className={localStyles.nameWrap}>
                <Fields.Text
                  tag={`firstName${optionalPopupSuffix}`}
                  required
                  schema={schemas.firstName}
                  label={cmsSubscribeTranslations.firstName}
                  props={{
                    className: localStyles.name,
                    disabled: submitted,
                  }}
                />
                <Fields.Text
                  tag={`lastName${optionalPopupSuffix}`}
                  required
                  schema={schemas.lastName}
                  label={cmsSubscribeTranslations.lastName}
                  props={{
                    className: localStyles.name,
                    disabled: submitted,
                  }}
                />
              </div>
              <Fields.Text
                tag={`email${optionalPopupSuffix}`}
                required
                schema={schemas.email}
                label={cmsSubscribeTranslations.email}
                props={{disabled: submitted}}
              />

              <div className={localStyles.displayNone}>
                <CountrySelect
                  tag="country"
                  required
                  initial={countryCode}
                  label="country"
                />
              </div>

              <div className={localStyles.displayNone}>
                <Fields.Text tag="campaignId" initial={campaignId} />
                <Fields.Text tag="eventName" initial={eventName} />
                <Fields.Text tag="productInterest" initial={productInterest} />
              </div>

              <div className={localStyles.displayNone}>
                <MktLangSelect
                  initial={language}
                  mkt={countryCode as (typeof availableCountries)[number]}
                  tag="preferredLang"
                  required
                  label="preferredLang"
                />
              </div>
              {(enablePhone || enablePostalCode) && (
                <div className={localStyles.nameWrap}>
                  {enablePhone && (
                    <div className={localStyles.phoneWrap}>
                      <PhoneCodeSelect
                        mkt={mkt}
                        tag={`phoneCode${optionalPopupSuffix}`}
                        props={{
                          className: FormStyles.phoneCode,
                          disabled: submitted,
                        }}
                        label={cmsSubscribeTranslations.phoneCode}
                      />
                      <PhoneField
                        tag={`phoneNumber${optionalPopupSuffix}`}
                        props={{
                          className: FormStyles.phoneNumber,
                          disabled: submitted,
                        }}
                        label={cmsSubscribeTranslations.phoneNumber}
                      />
                    </div>
                  )}
                  {enablePostalCode && (
                    <Fields.Text
                      tag={`postalCode${optionalPopupSuffix}`}
                      schema={schemas.postalCode}
                      label={cmsSubscribeTranslations.postalCode}
                      props={{
                        className: localStyles.postalCode,
                        disabled: submitted,
                        style: {textTransform: `uppercase`},
                      }}
                    />
                  )}
                </div>
              )}
            </fieldset>

            <Text variant="lgl-100">
              <Markdown
                className={
                  submitted ? localStyles.legalDisabled : localStyles.legal
                }
              >
                {transformMarkdown(
                  cmsSubscribeTranslations.legalConsent?.markdown || ``,
                )}
              </Markdown>
            </Text>

            <Button
              type="submit"
              variant={submitted ? `secondary` : `primary`}
              className={localStyles.button}
              loading={loading}
              disabled={(submitAttempt && hasErrors) || loading}
            >
              {submitted
                ? cmsSubscribeTranslations.signupAgain || `Sign up again`
                : cmsSubscribeTranslations.signup || `Sign up`}
            </Button>
          </form>
        </Fields.Wrapper>
      </div>
    </div>
  );

  return renderAsPopup ? (
    <PopupContainer topic="show-subscribe-popup" subscribeFunc={subscribe}>
      {subscribeForm}
    </PopupContainer>
  ) : (
    subscribeForm
  );
}
